import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Menu,
  Row,
  Col,
  Card,
  Carousel,
  Dropdown,
  Tabs,
  Typography,
  Image,
  Input,
  Button,
  Spin,
  notification,
  Tooltip,
  Modal,
} from 'antd';
import {
  CustomPageHeader,
  CustomFormItem,
  VehicleManagementDetailSkeleton,
  confirmDialog,
  useText
} from '@src/components';
import iconNpInfo from '@src/assets/icons/np_info.svg';
import iconInfo from '@src/assets/icons/icon-bilgi.svg';
import { LeftOutlined, RightOutlined, MoreOutlined } from '@ant-design/icons';
import {
  resetCurrentLoadAddCarPlateDetail,
  resetVehicleLimitWithValueTypes,
} from '@src/store/slice/addNewCarStore/addCarPlateDetailSlice';
import {
  getVehicleDetailList,
  setVehicleManagementItem,
  setActiveTab,
  finishLoadingTab,
  getUpdateVehicleDisable,
  deletePlateAndAllDevices,
  resetCurrentLoadVehicleManagementDetail,
} from '@src/store/slice/vehicleManagementStore/vehicleManagementDetailSlice';
import { useDispatch, useSelector } from 'react-redux';
import { formatPlate } from '@src/utils/utils';
import { useMediaQuery } from '@src/hooks/useMediaQuery';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import VehicleEditInformation from './VehicleEditInformation';
import VehicleDeviceOperations from './VehicleDeviceOperations';
import VehicleFuelHistory from './VehicleFuelHistory';
import VehicleDriverEditInformation from './VehicleDriverEditInformation';
import VehicleLimitDayRestrictionOperations from './VehicleLimitDayRestrictionOperations';
import PO from '@src/assets/icons/ticketcar/4.svg';
import TOTAL from '@src/assets/icons/ticketcar/41.svg';
import BP from '@src/assets/icons/ticketcar/42.svg';
import REFRESH from '@src/assets/icons/refresh.svg';
import '@src/styles/vehicleManagement/vehicleManagementDetail.scss';
import vehicleManagementDetailServices from '@src/services/vehicleManagement/vehicleManagementDetail.services';
import CustomImage from '@src/components/CustomImage';
import { getVehicleFuelTransaction } from '@src/store/slice/fuelConsumptionStore/fuelConsumptionSlice';
import { useLanguageChange } from '@src/hooks/useLanguageChange';
const { TabPane } = Tabs;
const { Title, Text } = Typography;
const getIconComponent = (providerType) => {
  switch (providerType) {
    case 4:
      return PO;
    case 41:
      return TOTAL;
    case 42:
      return BP;
    default:
      return null;
  }
};
function formatCurrency(value) {
  const formattedValue = new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    minimumFractionDigits: 0,
  }).format(value);
  return formattedValue.replace('₺', '') + '₺';
}
const MemoizedVehicleLimitDayRestrictionOperations = React.memo(
  VehicleLimitDayRestrictionOperations,
);
const MemoizedVehicleDeviceOperations = React.memo(VehicleDeviceOperations);
const MemoizedVehicleFuelHistorys = React.memo(VehicleFuelHistory);
const VehicleManagementDetail = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams();
  const { state } = useLocation();
  const isMobil = useMediaQuery({ query: '(max-width: 1199px)' });
  const [api, contextHolder] = notification.useNotification();
  const {
    activeTab,
    vehicleDetailList = [],
    vehicleManagementItem = {},
    loading,
  } = useSelector((state) => state.vehicleManagementDetail);
  const { isAdmin } = useSelector((state) => state.auth);
  const { selectedUserId } = useSelector((state) => state.user);

  // ---------------------------------------------------------------------
  // All translation lookups are hoisted here and called unconditionally,
  // in a fixed order, on every render. useText() is a Hook, so it must
  // never be called inside callbacks, loops, or conditional branches
  // (see Rules of Hooks). Everywhere else in this file we now reference
  // these plain string variables instead of calling useText() directly.
  // ---------------------------------------------------------------------
  const attentionText = useText('attention');
  const doYouApproveText = useText('doYouApprove');
  const yesText = useText('yes');
  const noText = useText('no');
  const giveUpText = useText('giveUp');
  const fuelPurchaseStoppedText = useText('allSelectedPlatesFuelPurchaseWillBeStopped') || 'İşlem gerçekleşti';
  const fuelPurchaseOpenedText = useText('allSelectedPlatesFuelPurchaseWillBeOpened') || 'İşlem gerçekleşti';
  const transactionSuccessText = useText('transactionSuccessfullyCompleted2');
  const areYouSureDeleteText = useText('areYouSureYouWantToDeleteThe');
  const deleteLicensePlateText = useText('deleteLicensePlateFromFleet');
  const renewCodeText = useText('renewCode');
  const deviceTypeText = useText('deviceType5') || 'Cihaz';
  const mobileApplicationText = useText('mobileApplication') || 'Mobil Uygulama';
  const cardText = useText('card') || 'Akaryakıt Kartı';
  const poActivationCodeText = useText('poActivationCode');
  const vehicleDetailText = useText('vehicleDetail');
  const vehicleManagementText = useText('vehicleManagement');
  const noLimitDeviceText = useText('noLimitDefinedForThisDevice');
  const totalLimitText = useText('totalLimit');
  const noLimitPlateText = useText('noLimitDefinedForThisLicensePlate');
  const remainingLimitText = useText('remainingLimit2');
  const activationCodeText = useText('activationCode');

const deviceFuelPurchaseWillBeStoppedForPlateText = useText('deviceFuelPurchaseWillBeStoppedForPlate');
  const deviceFuelPurchaseWillBeOpenedForPlateText = useText('deviceFuelPurchaseWillBeOpenedForPlate');
  const myEdenredRenewCodeWarningText =
    useText('myEdenredRenewCodeWarning') ||
    'MyEdenred {{activationCode}} yenilendiğinde {{plate}} plakalı aracın MyEdenred ile ilişiği kesilecektir. Yeni kod ile aracı tekrar MyEdenred’e ekleyebilirsiniz.';
  const driverCanUseCodeText = useText('theDriverCanUseThisCodeToAdd');
  const limitDayRestrictionOperations = useText('limitDayRestrictionOperations');
  const deviceOperations = useText('deviceOperations');
  const fuelHistory = useText('fuelHistory');
  const smartRingText = useText('smartRing');

  const [isDeviceOperationsLoaded, setIsDeviceOperationsLoaded] = useState(false);
  const [isLimitOperationsLoaded, setIsLimitOperationsLoaded] = useState(true);
  const [isFuelHistorysLoaded, setIsFuelHistorysLoaded] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [activationCode, setActivationCode] = useState('');
  const [vehicleManagementItemArray, setVehicleManagementItemArray] = useState([]);
  const [vehicleDetailLimitsForProviderTypes, setVehicleDetailLimitsForProviderTypes] = useState(
    [],
  );
  const [vehicleFuelHistories, setVehicleFuelHistories] = useState([]);
  const [historyPagination, setHistoryPagination] = useState({
    currentPage: 1,
    pageSize: 100,
    totalItems: 0,
  });
  const [remainLimit, setRemainLimit] = useState('');
  const [remainCurrency, setRemainCurrency] = useState('');
  const [detailLimitAmount, setDetailLimitAmount] = useState('');
  const [carouselIndex, setCarouselIndex] = useState(0);
  const isStatusAktif = state.statusLabel === 'Aktif';
  const previousIndexRef = useRef(0);
  const handleAfterChange = (currentIndex) => {
    previousIndexRef.current = currentIndex;
    setCarouselIndex(currentIndex);
  };
  const settings = {
    dots: false,
    arrows: true,
    prevArrow: (
      <div>
        <LeftOutlined
          style={{
            color: '#000',
            backgroundColor: '#f1f5f9',
            borderRadius: '50%',
            fontSize: '16px',
            padding: '5px',
          }}
          className="carousel-arrow left-arrow"
        />
      </div>
    ),
    nextArrow: (
      <div>
        <RightOutlined
          style={{
            color: '#000',
            backgroundColor: '#f1f5f9',
            borderRadius: '50%',
            fontSize: '16px',
            padding: '5px',
          }}
          className="carousel-arrow right-arrow"
        />
      </div>
    ),
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    adaptiveHeight: true,
    afterChange: handleAfterChange,
  };
  const { selectedCustomer } = useSelector((state) => state?.customer);
  const handleTabChange = async (key) => {
    dispatch(setActiveTab(key));
    if (key === '1' && !isLimitOperationsLoaded) {
      setIsLimitOperationsLoaded(true);
      setIsDeviceOperationsLoaded(false);
    }
    if (key === '2' && !isDeviceOperationsLoaded) {
      setIsDeviceOperationsLoaded(true);
      setIsLimitOperationsLoaded(false);
      dispatch(resetVehicleLimitWithValueTypes());
    }
    if (key === '3' && !isFuelHistorysLoaded) {
      setIsDeviceOperationsLoaded(false);
      setIsLimitOperationsLoaded(false);
      setIsFuelHistorysLoaded(true);
    }
    await new Promise((resolve) => setTimeout(resolve, 350));
    await dispatch(finishLoadingTab());
  };
  // İçerik render fonksiyonu
  const renderContent = (content) => {
    return loading.activeTab ? (
      <div className="loading-container" style={{ textAlign: 'center', padding: '200px' }}>
        <Spin size="large" className="custom-spin" />
      </div>
    ) : (
      content
    );
  };
  const fetchVehicleFuelHistories = async (
    pageNumber,
    pageSize,
    managementItem,
    detailList,
  ) => {
    const now = new Date();
    const sixMonthsAgo = new Date();

    const plate = detailList?.plate || managementItem?.plate;
    const vehicleId = managementItem?.vehicleId || detailList?.vehicleId;

    sixMonthsAgo.setMonth(now.getMonth() - 6);
    try {

      const body = {
        PageNumber: pageNumber,
        PageSize: pageSize,
        startDate: sixMonthsAgo.toISOString(),
        endDate: now.toISOString(),
        plateNumber: plate,
        vehicleId,
      };
      const response = await dispatch(getVehicleFuelTransaction(body));
      if (response?.payload?.data) {
        setVehicleFuelHistories(response.payload.data);
        setHistoryPagination((prev) => ({
          ...prev,
          currentPage: pageNumber,
          pageSize: pageSize,
          totalItems: response.payload.message ? Number.parseInt(response.payload.message, 10) : 0,
        }));
      }
    } catch (error) {
      console.error('Error fetching vehicle fuel histories:', error);
    }
    setIsFuelHistorysLoaded(true);
  };

  const fetchData = async () => {
    const managementItem = state;
    await dispatch(setVehicleManagementItem(managementItem));
    const response = await dispatch(
      getVehicleDetailList({
        customerId: isAdmin && selectedUserId
          ? selectedUserId
          : selectedCustomer.customerId,
        id: id || managementItem?.vehicleId,
        providerType: managementItem?.providerType,
      }),
    );

    return {
      managementItem,
      detailList: response?.payload?.data,
    };
  };
  // The vehicle detail and the fuel history rows carry backend labels (device type, status) and
  // are only loaded on mount, so they have to be requested again when the language is switched.
  useLanguageChange(async () => {
    const { managementItem, detailList } = await fetchData();
    await fetchVehicleFuelHistories(
      historyPagination.currentPage,
      historyPagination.pageSize,
      managementItem,
      detailList,
    );
  });

  useEffect(() => {
    const initializeData = async () => {
      const { managementItem, detailList } = await fetchData();

      await fetchVehicleFuelHistories(
        historyPagination.currentPage,
        historyPagination.pageSize,
        managementItem,
        detailList,
      );

      await handleTabChange(activeTab);
    };

    initializeData();
    return () => {
      dispatch(resetCurrentLoadAddCarPlateDetail());
      dispatch(resetCurrentLoadVehicleManagementDetail());
      dispatch(resetVehicleLimitWithValueTypes());
    };
  }, [dispatch]);
  useEffect(() => {
    if (vehicleDetailList) {
      const tempRemainLimit = vehicleDetailList.limitRemainingAmount
        ? vehicleDetailList.limitRemainingAmount + 'lt.'
        : '0 lt.';
      setRemainLimit(tempRemainLimit);
      const tempRemainCurrency = vehicleDetailList.limitRemainingAmount
        ? formatCurrency(vehicleDetailList.limitRemainingAmount)
        : '0 ₺';
      setRemainCurrency(tempRemainCurrency);
      const tempLimitAmount = vehicleDetailList.limitAmount
        ? formatCurrency(vehicleDetailList.limitAmount)
        : '0 ₺';
      setDetailLimitAmount(tempLimitAmount);
    }
  }, [vehicleDetailList]);
  useEffect(() => {
    if (selectedCustomer?.customerId && vehicleManagementItem?.vehicleId) getActivationCode();
  }, [vehicleManagementItem, selectedCustomer]);
  useEffect(() => {
    try {
      if (vehicleManagementItem?.multipleDevice && vehicleDetailList?.vehicleDetailLimits) {
        const vehicleDetailLimits = vehicleDetailList?.vehicleDetailLimits;
        const tempItem = vehicleDetailLimits?.filter(
          (item) => item.vehicleDetailId === vehicleManagementItem.vehicleDetailId,
        );
        let tempArr = tempItem ? tempItem : [vehicleManagementItem];
        vehicleManagementItem.multipleDevice.map((itemx) => {
          const temp = vehicleDetailLimits?.filter(
            (item) => item.vehicleDetailId === itemx.vehicleDetailId,
          );
          tempArr.push(temp[0]);
        });
        const filter18 = tempArr?.filter(
          (item) => item.deviceTypeId === 18 && item.status === 'Aktif',
        )[0];
        if (filter18 && tempArr[0].vehicleDetailId !== filter18.vehicleDetailId) {
          let newArr = [filter18];
          tempArr?.map((item) => {
            if (item.vehicleDetailId !== filter18.vehicleDetailId) {
              newArr.push(item);
            }
          });
          tempArr = newArr;
        }
        setVehicleManagementItemArray(tempArr);
      }
    } catch (error) {
      console.error(error);
    }
  }, [vehicleManagementItem, vehicleDetailList]);
  const getActivationCode = async () => {
    try {
      const data = {
        customerId: selectedCustomer.customerId,
        vehicleId: vehicleManagementItem.vehicleId,
      };
      const res = await vehicleManagementDetailServices.getVehicleActivationCode(data);
      if (res?.data?.activationKey) {
        setActivationCode(res?.data?.activationKey);
      } else {
        const newRes = await vehicleManagementDetailServices.createActivationCode(data);
        setActivationCode(newRes?.data?.activationKey);
      }
    } catch (error) {
      console.error(error);
    }
  };
  const refreshActivationCode = async () => {
    setIsModalVisible(true);
  };
  useEffect(() => {
    try {
      if (vehicleDetailList?.vehicleDetailLimits) {
        let tempVehicleDetailLimits = [];
        vehicleDetailList.vehicleDetailLimits.map((item) => {
          if (
            tempVehicleDetailLimits?.filter((temp) => temp.providerType === item.providerType)
              .length === 0
          ) {
            tempVehicleDetailLimits.push(item);
          }
        });
        setVehicleDetailLimitsForProviderTypes(tempVehicleDetailLimits);
      }
    } catch (error) {
      console.error(error);
    }
  }, [vehicleDetailList]);
  useEffect(() => {
    if (loading.activeTab) {
      const timeoutId = setTimeout(() => {
        dispatch(finishLoadingTab());
      }, 350);
      return () => clearTimeout(timeoutId);
    }
  }, [dispatch, activeTab, loading.activeTab]);
  const onPlateBasedFuelPurchase = async () => {
    confirmDialog({
      title: attentionText,
      htmlContent: isStatusAktif
        ? `${vehicleDetailList.plate} plakalı aracın üzerindeki tüm cihazlar yakıt alımına durdurulacaktır. ${doYouApproveText}`
        : `${vehicleDetailList.plate} plakalı aracın üzerindeki tüm cihazlar yakıt alımına açılacaktır. ${doYouApproveText}`,
      okText: yesText,
      cancelText: giveUpText,
      onOk: async () => {
        try {
          const request = {
            vehicleDetailIds: vehicleDetailList?.vehicleDeviceTypes?.map(
              (item) => item.vehicleDetailId,
            ),
            isDisabled: isStatusAktif,
            plate: vehicleDetailList.plate,
          };
          const response = await dispatch(getUpdateVehicleDisable({ request })).unwrap();
          if (response.isSuccess) {
            api.success({
              message: isStatusAktif ? fuelPurchaseStoppedText : fuelPurchaseOpenedText,
              description: transactionSuccessText,
              duration: 2,
            });
            setTimeout(() => {
              history.push('/ticket-car/vehicle-management');
            }, 3000);
          }
        } catch (error) {
          console.error('Validate Failed:', error);
        }
      },
    });
  };
  const onDeletePlateFromFleet = async () => {
    confirmDialog({
      title: attentionText,
      htmlContent: `${vehicleDetailList.plate} ${areYouSureDeleteText}`,
      okText: yesText,
      cancelText: giveUpText,
      onOk: async () => {
        try {
          const request = {
            plate: vehicleDetailList.plate,
            customerId: selectedCustomer?.customerId,
          };
          const response = await dispatch(deletePlateAndAllDevices({ request })).unwrap();
          if (response.isSuccess) {
            api.success({
              message: deleteLicensePlateText,
              description: transactionSuccessText,
              duration: 2,
            });
          }
          setTimeout(() => {
            history.push(
              isAdmin
                ? '/admin/ticket-car/management-car-processes'
                : '/ticket-car/vehicle-management',
            );
          }, 3000);
        } catch (error) {
          console.error('Validate Failed:', error);
        }
      },
    });
  };
  const menu = (
    <Menu className="custom-dropdown-menu">
      {/*<Menu.Item key="1" className="custom-dropdown-item" onClick={onPlateBasedFuelPurchase}>
        {isStatusAktif ? 'Aracın yakıt alımını geçici olarak durdur' : 'Yakıt Alımına Aç'}
      </Menu.Item>*/}
      <Menu.Item key="2" className="custom-dropdown-item" onClick={onDeletePlateFromFleet}>
        {deleteLicensePlateText}
      </Menu.Item>
    </Menu>
  );
  const handleModalOk = async () => {
    setIsModalVisible(false);
    try {
      const req = {
        customerId: selectedCustomer.customerId,
        plate: vehicleManagementItem.plate,
        vouId: vehicleManagementItem.providerType,
      };
      await vehicleManagementDetailServices.disconnectFromAccount(req);
      const data = {
        customerId: selectedCustomer.customerId,
        vehicleId: vehicleManagementItem.vehicleId,
      };
      const res = await vehicleManagementDetailServices.createActivationCode(data);
      if (res?.data?.activationKey) {
        api.success({
          message: renewCodeText,
          description: transactionSuccessText,
          duration: 2,
        });
        setActivationCode(res?.data?.activationKey);
      }
    } catch (error) {
      console.error(error);
    }
  };
  const handleModalCancel = () => {
    setIsModalVisible(false);
  };
  const getDeviceArea = (arr) => {
    return arr.map((item, index) => (
      <div key={index + 1} className="summary-content">
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            height: 85,
            justifyContent: 'space-between',
          }}
        >
          <div style={{ paddingLeft: 10 }}>
            <p className="ligth-text">{deviceTypeText}</p>
            <Title className="summary-value" style={{ textAlign: 'left' }}>
              {item.deviceTypeId === 17
                ? mobileApplicationText
                : item.deviceTypeId === 3
                  ? cardText
                  : item.deviceTypeId === 2
                    ? smartRingText
                    : item.deviceTypeId === 18
                      ? 'UTTS'
                      : ''}
            </Title>
            {item.deviceTypeId === 3 && item?.activationCode?.length > 0 ? (
              <div>
                <p className="ligth-text" style={{ marginTop: 5 }}>
                  {poActivationCodeText}{item?.activationCode}
                </p>
              </div>
            ) : null}
          </div>
          <div style={{ paddingRight: 10 }}>
            <Image
              src={getIconComponent(item.providerType)}
              alt={item.providerTypeLabel}
              preview={false}
              className="summary-icon"
            />
          </div>
        </div>
      </div>
    ));
  };
  const styleMarginBottom = useMemo(() => {
    return isMobil ? 10 : 20;
  }, [isMobil]);
  const styleFlexDirection = useMemo(() => {
    return isMobil ? 'column' : 'row';
  }, [isMobil]);
  const styleSpan = useMemo(() => {
    return isMobil ? '24' : '13';
  }, [isMobil]);
  const styleSpan2 = useMemo(() => {
    return isMobil ? '24' : '6';
  }, [isMobil]);
  const styleSpan3 = useMemo(() => {
    return isMobil ? '24' : '5';
  }, [isMobil]);
  const styleWidth = useMemo(() => {
    return isMobil ? '80%' : null;
  }, [isMobil]);
  const stylePaddingLeft = useMemo(() => {
    return isMobil ? 0 : 20;
  }, [isMobil]);
  const getLimitValue = (val) => {
    if (!val) return '';
    if (!val.limitAmount) return '';
    let type = ' tl.';
    if (val.limitValueType === 2) type = ' lt.';
    return val.limitAmount + type;
  };
  const getRemainLimitValue = (val) => {
    if (!val) return '';
    if (!val.limitRemainingAmount) return '';
    let type = ' tl.';
    if (val.limitValueType === 2) type = ' lt.';
    return val.limitRemainingAmount + type;
  };
  return (
    <CustomPageHeader
      data-testid="vehicle-management-detail-id"
      title={isAdmin ? null : <Text>{vehicleDetailText}</Text>}
      showBreadCrumb={!isAdmin}
      showHelpButton={!isAdmin}
      adminCustomHeaderContent={isAdmin ? 'custom-header-content-subpage' : ''}
      routes={['ticketCarHeader', vehicleManagementText]}
    >
      {contextHolder}
      <div className="vehicle-management-detail" data-testid="vehicle-management-detail-loading-id">
        {loading.vehicleDetailList || vehicleDetailList.length === 0 ? (
          <VehicleManagementDetailSkeleton activeTab={activeTab} />
        ) : (
          <>
            <Row className="summary-row" gutter={[16, 16]}>
              <Col
                className="summary-box"
                span={styleSpan}
                style={{ marginBottom: styleMarginBottom }}
              >
                <Card bordered={false}>
                  <div className="summary-content" style={{ flexDirection: styleFlexDirection }}>
                    <CustomFormItem
                      name={vehicleDetailList.plate}
                      className="monthly-amount-form-item"
                    >
                      <Input
                        className="box border-left custom-readonly-input"
                        defaultValue={formatPlate(vehicleDetailList.plate)}
                        maxLength={10}
                        readOnly
                        disabled
                      />
                    </CustomFormItem>
                    <div
                      className="ant-row"
                      style={{
                        flex: 1,
                        gap: 10,
                        alignItems: 'center',
                        justifyContent: 'center',
                        paddingLeft: stylePaddingLeft,
                        flexDirection: 'row',
                        width: styleWidth,
                      }}
                    >
                      {vehicleManagementItemArray?.length > 0 ? (
                        !vehicleManagementItemArray[carouselIndex]?.limitAmount ? (
                          <div className="limit-info" style={{ flex: 1, textAlign: 'center' }}>
                            <Text className="summary-title">{noLimitDeviceText}</Text>
                          </div>
                        ) : (
                          <div className="limit-info" style={{ flex: 1, textAlign: 'center' }}>
                            <Text className="summary-title">{totalLimitText}</Text>
                            <Title className="summary-value">
                              {vehicleManagementItemArray?.length > 0
                                ? getLimitValue(vehicleManagementItemArray[carouselIndex])
                                : vehicleDetailList?.limitValueType === 2
                                  ? vehicleDetailList.limitAmount + ' lt.'
                                  : detailLimitAmount}
                            </Title>
                          </div>
                        )
                      ) : !vehicleDetailList.limitAmount ? (
                        <div className="limit-info" style={{ flex: 1, textAlign: 'center' }}>
                          <Text className="summary-title">{noLimitPlateText}</Text>
                        </div>
                      ) : (
                        <div className="limit-info" style={{ flex: 1, textAlign: 'center' }}>
                          <Text className="summary-title">{totalLimitText}</Text>
                          <Title className="summary-value">
                            {vehicleManagementItemArray?.length > 0
                              ? getLimitValue(vehicleManagementItemArray[carouselIndex])
                              : vehicleDetailList?.limitValueType === 2
                                ? vehicleDetailList.limitAmount + ' lt.'
                                : detailLimitAmount}
                          </Title>
                        </div>
                      )}
                      {(vehicleManagementItemArray?.length > 0 &&
                        vehicleManagementItemArray[carouselIndex]?.limitAmount) ||
                        (!vehicleManagementItemArray?.length > 0 && vehicleDetailList.limitAmount) ? (
                        <div className="limit-info" style={{ flex: 1, textAlign: 'center' }}>
                          <Text className="summary-title">{remainingLimitText}</Text>
                          <Title className="summary-value">
                            {vehicleManagementItemArray?.length > 0
                              ? getRemainLimitValue(vehicleManagementItemArray[carouselIndex])
                              : vehicleDetailList?.limitValueType === 2
                                ? remainLimit
                                : remainCurrency}
                          </Title>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </Card>
              </Col>
              <Col
                className="summary-provider"
                span={styleSpan2}
                style={{ marginBottom: styleMarginBottom }}
              >
                <Card bordered={false}>
                  <Carousel {...settings} className="provider-carousel">
                    {vehicleManagementItemArray.length > 0
                      ? getDeviceArea(vehicleManagementItemArray)
                      : getDeviceArea([vehicleManagementItem])}
                  </Carousel>
                </Card>
              </Col>
              <Col className="summary-box" span={styleSpan3}>
                <Card bordered={false}>
                  <div className="summary-content">
                    <div className="activation-code-limit-info">
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Text className="summary-title">{activationCodeText}</Text>
                        <Tooltip
                          className="activation-info-button"
                          overlayClassName="activation-custom-tooltip"
                          align={{ offset: [180, 80] }}
                          title={
                            <div className="div-box">
                              <CustomImage src={iconNpInfo} style={{ width: 40, height: 40 }} />
                              <span style={{ color: '#000', fontWeight: 'normal', fontSize: 13 }}>
                                {driverCanUseCodeText}
                              </span>
                            </div>
                          }
                          color="#646363"
                          placement="top"
                          arrowPointAtCenter={true}
                        >
                          {''}
                          <CustomImage src={iconInfo} />
                        </Tooltip>
                      </div>
                      <Title className="activation-code-value">{activationCode}</Title>
                      <button
                        onClick={refreshActivationCode}
                        style={{ backgroundColor: 'transparent', marginTop: 5, borderWidth: 0 }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 5,
                          }}
                        >
                          <CustomImage src={REFRESH} />
                          <Text className="activation-code-refresh">{renewCodeText}</Text>
                        </div>
                      </button>
                    </div>
                  </div>
                </Card>
              </Col>
            </Row>
            <Row className="details-row" gutter={[16, 16]}>
              <Col className="card-box" span={16}>
                <VehicleEditInformation
                  vehicleDetailList={vehicleDetailList}
                  vehicleManagmentItem={state}
                />
              </Col>
              <Col className="card-box" span={8}>
                <VehicleDriverEditInformation
                  vehicleDetailList={vehicleDetailList}
                  vehicleManagmentItem={state}
                />
              </Col>
            </Row>
            <Tabs
              activeKey={activeTab}
              className="info-tabs"
              tabBarStyle={{ border: '0' }}
              style={{ paddingTop: '15px', backgroundColor: '#fff', borderRadius: '10px' }}
              onChange={handleTabChange}
              tabBarExtraContent={
                <Dropdown overlay={menu} trigger={['click']}>
                  <Button
                    type="text"
                    icon={<MoreOutlined />}
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      backgroundColor: '#f1f5f9',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '10px',
                      marginTop: '-10px',
                    }}
                  />
                </Dropdown>
              }
            >
              <TabPane tab={limitDayRestrictionOperations} key="1">
                {renderContent(
                  isLimitOperationsLoaded && (
                    <MemoizedVehicleLimitDayRestrictionOperations
                      vehicleListDetailLimits={vehicleDetailList.vehicleDetailLimits}
                      vehicleManagementItem={state || vehicleManagementItem}
                      vehicleDetailId={
                        carouselIndex === 0
                          ? vehicleManagementItem.vehicleDetailId
                          : vehicleManagementItem?.multipleDevice
                            ? vehicleManagementItem?.multipleDevice[carouselIndex - 1]
                              ?.vehicleDetailId
                            : vehicleDetailList.vehicleDetailId
                      }
                      vehicleId={state.vehicleId}
                      plate={vehicleDetailList.plate}
                      vehicleDeviceTypes={vehicleDetailList?.vehicleDeviceTypes}
                    />
                  ),
                )}
              </TabPane>
              <TabPane tab={deviceOperations} key="2">
                {renderContent(
                  isDeviceOperationsLoaded && (
                    <MemoizedVehicleDeviceOperations
                      vehicleListDetailDeviceTypes={vehicleDetailList.vehicleDeviceTypes}
                      deviceTypeId={vehicleDetailList.deviceTypeId}
                      vehicleManagmentItem={state}
                      detailListCount={vehicleDetailList.vehicleDeviceTypes?.length}
                    />
                  ),
                )}
              </TabPane>
              <TabPane tab={fuelHistory} key="3">
                {renderContent(
                  isFuelHistorysLoaded && (
                    <MemoizedVehicleFuelHistorys
                      vehicleFuelHistories={vehicleFuelHistories}
                      vehicleManagmentItem={state}
                      historyPagination={historyPagination}
                      setHistoryPagination={setHistoryPagination}
                      fetchVehicleFuelHistories={fetchVehicleFuelHistories}
                    />
                  ),
                )}
              </TabPane>
            </Tabs>
            <Modal
              title={renewCodeText}
              visible={isModalVisible}
              onOk={handleModalOk}
              onCancel={handleModalCancel}
              cancelText={noText}
              okText={yesText}
              centered={true}
              className="activation-modal"
              okButtonProps={{
                type: 'default',
              }}
              cancelButtonProps={{
                type: 'primary',
              }}
            >
              <p className="modal-text-normal">

{myEdenredRenewCodeWarningText
                  .replace('{{activationCode}}', activationCode)
                  .replace('{{plate}}', vehicleDetailList.plate)}

              </p>
              <p className="modal-text-normal">
                {doYouApproveText}
              </p>
            </Modal>
          </>
        )}
      </div>
    </CustomPageHeader>
  );
};
export default VehicleManagementDetail;